import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import { validateInvite, checkCoupon, createOrder } from '../utils/api';

const CRYPTO_OPTIONS = [
  { id: 'BTC', label: 'Bitcoin' },
  { id: 'ETH', label: 'Ethereum' },
  { id: 'LTC', label: 'Litecoin' },
  { id: 'USDT_ERC20', label: 'USDT ERC20' },
  { id: 'USDC_ERC20', label: 'USDC ERC20' },
  { id: 'USDT_BEP20', label: 'USDT BEP20' }
];

const ProductCard = ({ product, isPopular }) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(null);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const [email, setEmail] = useState('');
  const [serverInvite, setServerInvite] = useState('');
  const [coupon, setCoupon] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('LTC');
  const [selectedPayment, setSelectedPayment] = useState('crypto');
  const [paymentMethods, setPaymentMethods] = useState({ crypto: true, stripe: false, paypal: false });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [emailValid, setEmailValid] = useState(null);
  const [inviteStatus, setInviteStatus] = useState({ loading: false, valid: null, error: null, guild: null });
  const [couponStatus, setCouponStatus] = useState({ loading: false, valid: null, discount: null, type: null });
  const [orderLoading, setOrderLoading] = useState(false);

  // Load available payment methods
  useEffect(() => {
    fetch('/api/payment-methods')
      .then(r => r.json())
      .then(data => {
        setPaymentMethods(data);
        // Default to first available method
        if (data.stripe) setSelectedPayment('stripe');
        else if (data.paypal) setSelectedPayment('paypal');
        else setSelectedPayment('crypto');
      })
      .catch(() => {});
  }, []);

  const debouncedInvite = useDebounce(serverInvite, 2000);

  const isOutOfStock = product.type === 'tool' ? false : product.stock === 0;
  const currentStock = product.type === 'tool' ? 999 : (product.stock || 0);
  const pricePerBoost = product.pricePerBoost || 0.20;
  const isTokenProduct = product.type === 'token';
  const isToolProduct = product.type === 'tool';

  // Tool product feature data
  const TOOL_DATA = {
    'tool_boost_bot': {
      tagline: 'Full-featured Discord boost bot with autobuy & dashboard',
      features: ['SellAuth & Paylix auto-delivery', 'OAuth2 boosting with captcha bypass', 'Custom watermarks (avatar, banner, decoration, profile effect)', 'Live stock & order status webpages', 'Redeem key system with custom panels', 'Web dashboard with full domain support'],
      modal: {
        title: 'Boost Bot',
        subtitle: 'Full-Featured Discord Boost Bot with Autobuy & Dashboard',
        intro: 'Run your entire boost operation from Discord with the Boost Bot — a fully automated Discord bot with autobuy integration, watermarking, live stock pages, redeem panels, and a complete web dashboard. Designed for resellers who want everything in one place.',
        sections: [
          { emoji: '⚙️', heading: 'Full Config Customisation', items: ['Custom Nickname, Bio, Avatar, Banner', 'Custom Avatar Decoration & Profile Effect', 'Custom Pronouns & Status', 'Custom RPC with buttons and images', 'Separate stock handling for 1-month & 3-month tokens', 'Proxy support for stability'] },
          { emoji: '🤖', heading: 'Captcha Solving', items: ['RazorCap support', 'OnyxSolver support'] },
          { emoji: '🛒', heading: 'AutoBuy Integration', items: ['Full SellAuth & Paylix auto-delivery', 'Auto-Hold when stock is low, auto-unhold when restocked', 'Auto-fetch products with /setup-autobuy command', 'Custom field mapping for Invite, Name, Bio', 'Formatted delivery messages with order status links'] },
          { emoji: '🌐', heading: 'Web Dashboard & Pages', items: ['Live stock page — real-time boost availability <a href="https://bot.outsourced.rip/989843/stock" target="_blank" rel="noopener" style="color:#2ee8c5;text-decoration:none">preview</a>', 'Order status pages — customers can track their order <a href="https://bot.outsourced.rip/989843/order/0261892" target="_blank" rel="noopener" style="color:#2ee8c5;text-decoration:none">preview</a>', 'Redeem panel — customers redeem boost keys <a href="https://bot.outsourced.rip/989843/redeem" target="_blank" rel="noopener" style="color:#2ee8c5;text-decoration:none">preview</a>', 'Full admin dashboard with domain integration', 'Public suggestion board for customer feedback'] },
          { emoji: '💡', heading: 'Extra Features', items: ['Multi-Server Boosting — boost multiple servers in one go', 'Unboost — remove boosts from a server by order', 'Token Checker with detailed reports', 'Redeem key system with custom embed panels', 'OAuth2 Boosting for captcha bypass', 'Token onliner — keeps boosted tokens online automatically', 'Advanced boost logs with Copy Tokens & Order JSON', 'Discord Webhook support for all events', 'HWID-locked licensing via GreenAuth'] },
        ],
        footer: 'Everything you need to run a boost reselling business — automated orders, branded profiles, live pages, and full remote management from Discord.'
      }
    },
    'tool_boost_tool': {
      tagline: 'Instant, reliable & fully automated Discord boosting',
      features: ['Multi-server boosting', 'AutoBuy integration (SellAuth)', 'Captcha solving (RazorCap, OnyxSolver)', 'Custom nickname, bio, avatar, banner', 'Auto-restock & unboost', 'Proxy support & proxyless mode'],
      modal: {
        title: 'Boost Tool',
        subtitle: 'Instant, Reliable & Fully Automated Discord Boosting',
        intro: 'Boost any server in seconds with the Boost Tool — a powerful, high-performance boosting solution built for speed and automation. Designed for resellers and server owners, it handles everything from single boosts to full autobuy integration — no hosting required.',
        sections: [
          { emoji: '⚙️', heading: 'Full Config Customisation', items: ['Custom Nickname, Bio, Avatar, Banner', 'Separate stock handling for 1-month & 3-month tokens', 'Proxy support for stability', 'Proxyless mode available (not recommended for heavy use)'] },
          { emoji: '🤖', heading: 'Captcha Solving', items: ['RazorCap support', 'OnyxSolver support'] },
          { emoji: '🛒', heading: 'AutoBuy Integration', items: ['Full SellAuth integration for automated purchasing', 'Auto-Hold with configurable delay', 'Custom field mapping for Invite, Nickname, Bio', 'Auto Restock tokens directly from the tool'] },
          { emoji: '💡', heading: 'Extra Features', items: ['Multi-Server Boosting — boost multiple servers in one go', 'Unboost — remove boosts from a server', 'Stock Checker — view available 1m/3m tokens at a glance', 'Discord Webhook support for live updates', 'No hosting required — run directly from your device', 'Clean, user-friendly interface', 'Fast boosting with multi-threading', 'HWID-locked licensing via GreenAuth'] },
        ],
        footer: 'Whether you\'re selling boosts or powering up your own servers, Boost Tool gives you full control, speed, and performance in one clean package.'
      }
    },
    'tool_token_checker': {
      tagline: 'Fast, detailed & multi-threaded Discord token verification',
      features: ['Nitro status & boost info detection', 'Token age & verification level checks', 'Flagged/locked token identification', 'Cooldown & subscription tracking', 'Color-coded output with summaries', 'Multi-threaded — hundreds per minute'],
      modal: {
        title: 'Token Checker',
        subtitle: 'Fast, Detailed & Multi-Threaded Discord Token Verification',
        intro: 'Instantly verify thousands of Discord tokens with the Token Checker — a lightweight, blazing-fast tool that gives you a full breakdown of every token in your stock. Know exactly what you\'re working with before you sell or boost.',
        sections: [
          { emoji: '⚙️', heading: 'Configurable Checks', items: ['Nitro Status — detect active Nitro subscriptions', 'Token Age — see how old the account is', 'Verification Level — Email, Phone, or Fully Verified', 'Flagged Detection — identify flagged or locked tokens', 'Boost Info — available boosts, transferable status, days remaining', 'Subscription Status — subscribed vs. not subscribed', 'Cooldown Check — redeemable boost cooldown tracking'] },
          { emoji: '📊', heading: 'Detailed Output', items: ['Color-coded flags per token: [FULLY_VERIFIED] [NITRO] [2_BOOSTS]', 'Clean summary with totals: Valid, Invalid, Locked, EV/PV/FV', 'Nitro count, Boost count & more', 'Results saved to Output folder automatically'] },
          { emoji: '💡', heading: 'Extra Features', items: ['Multi-threaded — check hundreds of tokens per minute', 'Proxy support or Proxyless mode', 'Discord Webhook notifications (if configured)', 'No hosting required — run directly from your device', 'Clean, icon-based console UI', 'HWID-locked licensing via GreenAuth'] },
        ],
        footer: 'Stop guessing your stock quality. Token Checker gives you a full breakdown in seconds so you always know what you\'re selling.'
      }
    },
    'tool_password_changer': {
      tagline: 'Bulk Discord token password rotation in seconds',
      features: ['Custom password format with wildcards', 'Configurable threads & delay', 'Auto-retry on rate limits & errors', 'Fresh token capture after change', 'Smart input (email:pass:token)', 'Webhook with zip file delivery'],
      modal: {
        title: 'Password Changer',
        subtitle: 'Bulk Discord Token Password Rotation in Seconds',
        intro: 'Secure your token stock instantly with the Password Changer — a fast, multi-threaded tool that rotates passwords across all your tokens in bulk. Refreshes tokens automatically so your stock stays clean and ready to use.',
        sections: [
          { emoji: '⚙️', heading: 'Full Config Customisation', items: ['Custom password format — use * wildcards for random characters', 'Configurable thread count for speed control', 'Changer delay — throttle requests to avoid rate limits', 'Proxy support for stability', 'Dated output folders for organized results'] },
          { emoji: '📂', heading: 'Smart Input & Output', items: ['Supports email:pass:token and pass:token formats', 'Optional promo tag passthrough (e.g. token|promo)', 'Auto-removes processed tokens from input file', 'Results split into success.txt, failed.txt, error.txt'] },
          { emoji: '💡', heading: 'Extra Features', items: ['Multi-threaded — change hundreds of passwords per minute', 'Discord Webhook with zip file delivery of results', 'Auto-retry on rate limits, timeouts, and proxy errors', 'New token capture — outputs fresh token after password change', 'No hosting required — run directly from your device', 'Clean, icon-based console UI', 'HWID-locked licensing via GreenAuth'] },
        ],
        footer: 'Keep your stock fresh and secure. Password Changer rotates passwords in bulk so your tokens stay valid and ready for action.'
      }
    }
  };

  const toolInfo = isToolProduct ? (TOOL_DATA[product.id] || null) : null;
  const [showToolDetails, setShowToolDetails] = useState(false);
  const toolTiers = isToolProduct && product.tiers && product.tiers.length > 0 ? product.tiers : null;
  const [selectedTier, setSelectedTier] = useState(0);

  const allOptions = product.allBoostOptions || [2,4,6,8,10,12,14,16,18,20,22,24,26,28,30];
  const defaultOptions = product.boostOptions || [2,8,14,20,30];
  
  const boostOptions = showAllOptions ? allOptions : defaultOptions;
  const hasMoreOptions = allOptions.length > defaultOptions.length;

  useEffect(() => {
    if (quantity === null || quantity > currentStock) {
      const availableOptions = defaultOptions.filter(opt => opt <= currentStock);
      if (availableOptions.length > 0) {
        setQuantity(availableOptions[0]);
      } else if (currentStock > 0) {
        const allAvailable = allOptions.filter(opt => opt <= currentStock);
        if (allAvailable.length > 0) {
          setQuantity(allAvailable[0]);
        } else {
          setQuantity(currentStock);
        }
      } else {
        setQuantity(2);
      }
    }
  }, [currentStock, quantity, defaultOptions, allOptions]);

  const effectiveQuantity = quantity || (isTokenProduct || isToolProduct ? 1 : 2);
  const currentTierPrice = toolTiers ? toolTiers[selectedTier]?.price || pricePerBoost : pricePerBoost;
  const subtotal = effectiveQuantity * currentTierPrice;
  
  let couponDiscount = 0;
  if (couponStatus.valid) {
    couponDiscount = couponStatus.type === 'percentage' 
      ? subtotal * (couponStatus.discount / 100) 
      : couponStatus.discount;
  }
  
  // Minimum amounts per crypto
  const CRYPTO_MINIMUMS = {
    'BTC': 5.00,
    'ETH': 3.00,
    'LTC': 0.50,
    'USDT_ERC20': 1.00,
    'USDC_ERC20': 1.00,
    'USDT_BEP20': 0.50
  };
  
  const baseTotal = subtotal - couponDiscount;
  const minAmount = CRYPTO_MINIMUMS[selectedCrypto] || 0.50;
  const minimumFee = selectedPayment === 'crypto' && baseTotal < minAmount ? minAmount - baseTotal : 0;
  const cardFee = selectedPayment === 'stripe' && baseTotal < 5 ? 0.50 : 0;
  const total = baseTotal + minimumFee + cardFee;
  const displayTotal = Math.max(0, baseTotal); // shown on card (no fees until payment method picked)

  const validateEmailFormat = (em) => em.includes('@') && em.length > 3;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailValid(value.length > 0 ? validateEmailFormat(value) : null);
  };

  const validateInviteLink = useCallback(async (invite) => {
    if (!invite || invite.length < 2) {
      setInviteStatus({ loading: false, valid: null, error: null, guild: null });
      return;
    }
    setInviteStatus({ loading: true, valid: null, error: null, guild: null });
    try {
      const result = await validateInvite(invite);
      setInviteStatus(result.valid 
        ? { loading: false, valid: true, error: null, guild: result.guild }
        : { loading: false, valid: false, error: result.error, guild: null });
    } catch (err) {
      setInviteStatus({ loading: false, valid: false, error: 'Failed to validate invite', guild: null });
    }
  }, []);

  useEffect(() => {
    if (debouncedInvite) validateInviteLink(debouncedInvite);
  }, [debouncedInvite, validateInviteLink]);

  const handleInviteChange = (e) => {
    const value = e.target.value;
    setServerInvite(value);
    setInviteStatus(value.length > 0 
      ? { loading: true, valid: null, error: null, guild: null }
      : { loading: false, valid: null, error: null, guild: null });
  };

  const handleCouponCheck = async () => {
    if (!coupon) return;
    setCouponStatus({ loading: true, valid: null, discount: null, type: null });
    try {
      const result = await checkCoupon(coupon);
      setCouponStatus(result.valid 
        ? { loading: false, valid: true, discount: result.discount, type: result.type }
        : { loading: false, valid: false, discount: null, type: null });
    } catch (err) {
      setCouponStatus({ loading: false, valid: false, discount: null, type: null });
    }
  };

  const canOpenModal = !isOutOfStock && emailValid && (isTokenProduct || isToolProduct || (inviteStatus.valid && !inviteStatus.loading));
  const canCreateOrder = termsAccepted && !orderLoading;

  const handleBuyNow = () => { 
    if (canOpenModal) setShowModal(true); 
  };

  const handleCreateOrder = async () => {
    if (!canCreateOrder) return;
    
    // Stripe minimum $1
    if (selectedPayment === 'stripe' && total < 1) {
      alert('Minimum order for card payment is $1.00');
      return;
    }
    
    setOrderLoading(true);
    try {
      const orderData = {
        email, 
        serverInvite: (isTokenProduct || isToolProduct) ? '' : serverInvite, 
        productId: product.id, 
        quantity: effectiveQuantity,
        coupon: couponStatus.valid ? coupon : null,
        selectedDuration: toolTiers ? toolTiers[selectedTier]?.duration : null
      };
      
      if (selectedPayment === 'stripe') {
        // Create Stripe checkout
        const res = await fetch('/api/create-stripe-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderData)
        });
        const result = await res.json();
        if (result.url) {
          window.location.href = result.url;
        } else {
          throw new Error(result.error || 'Failed to create Stripe checkout');
        }
      } else if (selectedPayment === 'paypal') {
        // Create PayPal order
        const res = await fetch('/api/create-paypal-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderData)
        });
        const result = await res.json();
        if (result.url) {
          window.location.href = result.url;
        } else {
          throw new Error(result.error || 'Failed to create PayPal order');
        }
      } else {
        // Crypto payment (existing flow)
        const result = await createOrder({ ...orderData, cryptoType: selectedCrypto });
        if (result.success) {
          window.open(`/checkout/${result.orderId}`, '_blank');
          resetModal();
        }
      }
    } catch (err) {
      alert(err.message || 'Failed to create order');
    } finally {
      setOrderLoading(false);
    }
  };

  const resetModal = () => { 
    setShowModal(false); 
    setTermsAccepted(false); 
  };

  return (
    <>
      <div className={`product-card ${isPopular ? 'popular' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}>
        
        {/* Card glow effect */}
        <div className="pc-glow"></div>

        {isPopular && (
          <div className="popular-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Most Popular
          </div>
        )}
        
        <div className="product-header">
          <div>
            <div className="product-title">{product.product}</div>
            <div className="product-type">{isToolProduct ? 'Software License' : isTokenProduct ? 'Discord Nitro Token' : 'Server Boosts'}</div>
          </div>
          <span className={`stock-badge ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`}>
            <span className="stock-dot"></span>
            {isToolProduct ? 'Available' : isOutOfStock ? 'Out of stock' : `${product.stock} in stock`}
          </span>
        </div>

        {/* Tier indicators */}
        {!isTokenProduct && !isToolProduct && (
        <div className="pc-tier-bar">
          <span className={`pc-tier ${effectiveQuantity >= 2 ? 'pc-tier-on' : ''}`}>Level 1</span>
          <span className={`pc-tier ${effectiveQuantity >= 7 ? 'pc-tier-on' : ''}`}>Level 2</span>
          <span className={`pc-tier ${effectiveQuantity >= 14 ? 'pc-tier-on pc-tier-max' : ''}`}>Level 3</span>
        </div>
        )}

        <div className="boost-selector">
          <div className="boost-selector-label">Select {isToolProduct ? 'licenses' : isTokenProduct ? 'tokens' : 'boosts'}</div>
          {isToolProduct ? (
            <div className="token-qty-wrap">
              {/* Tagline */}
              {toolInfo && <div style={{ fontSize: '.74rem', color: 'var(--text-secondary)', marginBottom: 10, textAlign: 'center', lineHeight: 1.5 }}>{toolInfo.tagline}</div>}
              {!toolInfo && product.description && <div style={{ fontSize: '.74rem', color: 'var(--text-secondary)', marginBottom: 10, textAlign: 'center', lineHeight: 1.5 }}>{product.description}</div>}

              {/* Key features */}
              {toolInfo && (
                <div style={{ marginBottom: 10 }}>
                  {toolInfo.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: '.72rem', color: 'var(--text-muted)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                      {f}
                    </div>
                  ))}
                </div>
              )}

              {/* View details button */}
              {toolInfo && (
                <div style={{ marginBottom: 10, textAlign: 'center' }}>
                  <button 
                    onClick={() => setShowToolDetails(true)} 
                    style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '.7rem', cursor: 'pointer', fontWeight: 600, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    View details →
                  </button>
                </div>
              )}

              {/* Tier selector */}
              {toolTiers && toolTiers.length > 1 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>Select license duration</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {toolTiers.map((tier, i) => {
                      const durLabels = {'lifetime':'Lifetime','1d':'1 Day','3d':'3 Days','7d':'Weekly','14d':'2 Weeks','30d':'Monthly','60d':'60 Days','90d':'90 Days','180d':'6 Months','365d':'Yearly'};
                      return (
                        <button 
                          key={i}
                          onClick={() => setSelectedTier(i)}
                          style={{ 
                            flex: '1 1 auto', minWidth: 70, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', 
                            textAlign: 'center', fontSize: '.7rem', fontWeight: 600, transition: 'all .15s',
                            background: selectedTier === i ? 'rgba(46,232,197,.1)' : 'rgba(255,255,255,.03)',
                            border: selectedTier === i ? '1px solid rgba(46,232,197,.3)' : '1px solid rgba(255,255,255,.08)',
                            color: selectedTier === i ? 'var(--accent)' : 'var(--text-muted)'
                          }}
                        >
                          <div>{durLabels[tier.duration] || tier.duration}</div>
                          <div style={{ fontSize: '.65rem', marginTop: 2, opacity: .8 }}>${tier.price.toFixed(2)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Single tier badge */}
              {toolTiers && toolTiers.length === 1 && (
                <div style={{ fontSize: '.68rem', color: 'var(--accent)', marginBottom: 8, textAlign: 'center', fontWeight: 600 }}>
                  License: {({'lifetime':'♾️ Lifetime','1d':'1 Day','3d':'3 Days','7d':'7 Days','14d':'14 Days','30d':'30 Days','60d':'60 Days','90d':'90 Days','180d':'6 Months','365d':'1 Year'})[toolTiers[0].duration] || toolTiers[0].duration}
                </div>
              )}

              {/* Quantity selector */}
              <div className="token-qty-input">
                <button className="token-qty-btn" onClick={() => setQuantity(Math.max(1, (quantity || 1) - 1))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <input 
                  type="number" 
                  className="token-qty-field"
                  value={quantity || 1}
                  min={1}
                  max={10}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(parseInt(e.target.value) || 1, 10)))}
                />
                <button className="token-qty-btn" onClick={() => setQuantity(Math.min(10, (quantity || 1) + 1))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
            </div>
          ) : isTokenProduct ? (
            <div className="token-qty-wrap">
              <div className="token-qty-input">
                <button className="token-qty-btn" onClick={() => setQuantity(Math.max(1, (quantity || 1) - 1))} disabled={isOutOfStock}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <input 
                  type="number" 
                  className="token-qty-field"
                  value={quantity || 1}
                  min={1}
                  max={currentStock}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) || 1;
                    setQuantity(Math.max(1, Math.min(v, currentStock)));
                  }}
                  disabled={isOutOfStock}
                />
                <button className="token-qty-btn" onClick={() => setQuantity(Math.min(currentStock, (quantity || 1) + 1))} disabled={isOutOfStock}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
              <div className="token-qty-presets">
                {[1, 5, 10, 25, 50].filter(n => n <= currentStock).map(n => (
                  <button key={n} className={`token-preset ${effectiveQuantity === n ? 'active' : ''}`} onClick={() => setQuantity(n)}>{n}</button>
                ))}
              </div>
            </div>
          ) : (
          <div className="boost-options">
            {boostOptions.map((opt) => (
              <button 
                key={opt} 
                className={`boost-option ${effectiveQuantity === opt ? 'selected' : ''} ${opt > currentStock ? 'unavailable' : ''}`}
                onClick={() => setQuantity(opt)} 
                disabled={isOutOfStock || opt > currentStock}
              >
                {opt}
              </button>
            ))}
          </div>
          )}
          {!isTokenProduct && !isToolProduct && hasMoreOptions && (
            <button className="show-more-btn" onClick={() => setShowAllOptions(!showAllOptions)}>
              {showAllOptions ? 'Show Less ▲' : 'Show More ▼'}
            </button>
          )}
        </div>

        <div className="price-display">
          <span className="price-amount">${displayTotal.toFixed(2)}</span>
          <div className="price-meta">
            <span className="price-label">total</span>
            <span className="price-per">${currentTierPrice.toFixed(2)}/{isToolProduct ? 'license' : isTokenProduct ? 'token' : 'boost'}</span>
          </div>

        </div>

        <div className="pc-divider"></div>

        <div className="features-list">
          <div className="feature-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {isToolProduct ? 'HWID-locked licensing' : 'Instant delivery'}
          </div>
          <div className="feature-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {isToolProduct ? 'No hosting required' : 'Secure payment'}
          </div>
          <div className="feature-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            24/7 support
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Email <span className="required">*</span></label>
          <input 
            type="email" 
            className={`form-input ${emailValid === false ? 'error' : ''} ${emailValid === true ? 'success' : ''}`}
            placeholder="your@email.com" 
            value={email} 
            onChange={handleEmailChange} 
            disabled={isOutOfStock}
          />
          {emailValid === false && (
            <div className="input-message error">✕ Please enter a valid email address</div>
          )}
        </div>

        {!isTokenProduct && !isToolProduct && (
        <div className="form-group">
          <label className="form-label">Discord Server Invite <span className="required">*</span></label>
          <input 
            type="text" 
            className={`form-input ${inviteStatus.valid === false ? 'error' : ''} ${inviteStatus.valid === true ? 'success' : ''}`}
            placeholder="discord.gg/invite or invite code" 
            value={serverInvite} 
            onChange={handleInviteChange} 
            disabled={isOutOfStock}
          />
          {inviteStatus.loading && (
            <div className="input-message loading">
              <span className="loading-spinner"></span> Validating invite...
            </div>
          )}
          {inviteStatus.valid === false && (
            <div className="input-message error">✕ {inviteStatus.error || 'Invalid invite link'}</div>
          )}
          {inviteStatus.valid === true && inviteStatus.guild && (
            <div className="server-info">
              <div className="server-icon">
                {inviteStatus.guild.icon 
                  ? <img src={`https://cdn.discordapp.com/icons/${inviteStatus.guild.id}/${inviteStatus.guild.icon}.png`} alt={inviteStatus.guild.name}/>
                  : '🎮'}
              </div>
              <div className="server-details">
                <div className="server-name">{inviteStatus.guild.name}</div>
                <div className="server-members">{inviteStatus.guild.memberCount?.toLocaleString()} members</div>
              </div>
            </div>
          )}
        </div>
        )}

        <button 
          className={`btn btn-primary ${isOutOfStock ? 'btn-unavailable' : ''}`} 
          onClick={handleBuyNow} 
          disabled={isOutOfStock || !canOpenModal}
        >
          {isOutOfStock ? 'Currently Unavailable' : (
            <>
              Buy Now — ${displayTotal.toFixed(2)}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </>
          )}
        </button>
        {isOutOfStock && (
          <span className="btn-unavailable-reason">Out of stock — check back soon</span>
        )}

        {/* Auto-Reboost Upsell Banner */}
        {!isTokenProduct && !isToolProduct && (
        <div className="pc-reboost-banner" onClick={() => {
          const user = localStorage.getItem('user');
          if (user) {
            navigate('/dashboard?tab=reboost');
          } else {
            navigate('/login');
          }
        }}>
          <div className="pc-reboost-banner-glow"></div>
          <div className="pc-reboost-banner-content">
            <div className="pc-reboost-banner-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            </div>
            <div className="pc-reboost-banner-text">
              <span className="pc-reboost-banner-title">Auto-Reboost<span className="pc-reboost-banner-spark">⚡</span></span>
              <span className="pc-reboost-banner-desc">Never lose your boosts — save up to <strong>25%</strong></span>
            </div>
            <svg className="pc-reboost-banner-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </div>
        </div>
        )}
      </div>

      {/* Tool Details Modal */}
      {showToolDetails && toolInfo?.modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowToolDetails(false)}>
          <div style={{ background: 'linear-gradient(160deg, #0d1117 0%, #0a0e14 50%, #080c10 100%)', border: '1px solid rgba(46,232,197,.2)', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,.6), 0 0 40px rgba(46,232,197,.08)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(46,232,197,.15)', background: 'linear-gradient(135deg, rgba(46,232,197,.06), rgba(46,232,197,.01))', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>{toolInfo.modal.title}</h2>
              <button onClick={() => setShowToolDetails(false)} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#888', transition: 'all .15s' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Description label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ width: 3, height: 18, borderRadius: 2, background: 'var(--accent, #2ee8c5)', flexShrink: 0 }}></span>
                <span style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--accent, #2ee8c5)' }}>Product Description</span>
              </div>
              {/* Subtitle + Intro */}
              <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--accent, #2ee8c5)', marginBottom: 8 }}>🚀 {toolInfo.modal.subtitle}</div>
                <div style={{ fontSize: '.78rem', color: '#b0b0bc', lineHeight: 1.65 }}>{toolInfo.modal.intro}</div>
              </div>
              {/* Sections */}
              {toolInfo.modal.sections.map((sec, si) => (
                <div key={si} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--accent, #2ee8c5)', marginBottom: 8 }}>{sec.emoji} {sec.heading}:</div>
                  <div style={{ paddingLeft: 4 }}>
                    {sec.items.map((item, ii) => (
                      <div key={ii} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: '.76rem', color: '#b0b0bc', lineHeight: 1.5 }}>
                        <span style={{ color: 'var(--accent, #2ee8c5)', flexShrink: 0, marginTop: 1 }}>•</span>
                        <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#e0e0ec">$1</strong>') }}></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {/* Footer */}
              {toolInfo.modal.footer && (
                <>
                  <div style={{ height: 1, background: 'rgba(255,255,255,.06)', margin: '12px 0 14px' }}></div>
                  <div style={{ fontSize: '.76rem', color: '#b0b0bc', lineHeight: 1.6 }}>{toolInfo.modal.footer}</div>
                  <div style={{ fontSize: '.74rem', color: '#888', marginTop: 10 }}>
                    Need help getting started? <a href="https://discord.gg/outsourced" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent, #2ee8c5)', textDecoration: 'none', fontWeight: 600 }}>Open a ticket</a> — we're happy to help.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{effectiveQuantity}x [{product.product}] - ${total.toFixed(2)}</h2>
              <button className="modal-close" onClick={resetModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              {/* Payment Method Tabs */}
              <div className="payment-method-tabs" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Payment Method</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {paymentMethods.stripe && (
                    <button
                      className={`crypto-option ${selectedPayment === 'stripe' ? 'selected' : ''}`}
                      onClick={() => setSelectedPayment('stripe')}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      Card
                    </button>
                  )}
                  {paymentMethods.paypal && (
                    <button
                      className={`crypto-option ${selectedPayment === 'paypal' ? 'selected' : ''}`}
                      onClick={() => setSelectedPayment('paypal')}
                      style={{ flex: 1 }}
                    >
                      PayPal
                    </button>
                  )}
                  {paymentMethods.crypto && (
                    <button
                      className={`crypto-option ${selectedPayment === 'crypto' ? 'selected' : ''}`}
                      onClick={() => setSelectedPayment('crypto')}
                      style={{ flex: 1 }}
                    >
                      Crypto
                    </button>
                  )}
                </div>
              </div>

              {/* Crypto Options - Only show when crypto selected */}
              {selectedPayment === 'crypto' && (
                <div className="crypto-selector">
                  <label className="form-label">Select Cryptocurrency</label>
                  <div className="crypto-options">
                    {CRYPTO_OPTIONS.map((crypto) => (
                      <button 
                        key={crypto.id} 
                        className={`crypto-option ${selectedCrypto === crypto.id ? 'selected' : ''}`}
                        onClick={() => setSelectedCrypto(crypto.id)}
                      >
                        {crypto.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Coupon Code</label>
                <div className="coupon-row">
                  <input 
                    type="text" 
                    className={`form-input ${couponStatus.valid === false ? 'error' : ''} ${couponStatus.valid === true ? 'success' : ''}`}
                    placeholder="Enter coupon code" 
                    value={coupon}
                    onChange={(e) => { 
                      setCoupon(e.target.value); 
                      setCouponStatus({ loading: false, valid: null, discount: null, type: null }); 
                    }}
                  />
                  <button 
                    className="coupon-btn" 
                    onClick={handleCouponCheck} 
                    disabled={!coupon || couponStatus.loading}
                  >
                    {couponStatus.loading ? '...' : 'Apply'}
                  </button>
                </div>
                {couponStatus.valid === false && (
                  <div className="input-message error">✕ Invalid coupon</div>
                )}
                {couponStatus.valid === true && (
                  <div className="input-message success">
                    ✓ Coupon applied: {couponStatus.discount}{couponStatus.type === 'percentage' ? '%' : '$'} off
                  </div>
                )}
              </div>

              <div className="price-summary">
                <div className="price-row">
                  <span className="label">{effectiveQuantity}x {isTokenProduct ? 'Tokens' : 'Boosts'}</span>
                  <span className="value">${subtotal.toFixed(2)}</span>
                </div>
                {couponStatus.valid && couponDiscount > 0 && (
                  <div className="price-row">
                    <span className="label">Coupon Discount</span>
                    <span className="value discount">-${couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                {minimumFee > 0 && (
                  <div className="price-row">
                    <span className="label">Min. Order Fee ({selectedCrypto})</span>
                    <span className="value fee">+${minimumFee.toFixed(2)}</span>
                  </div>
                )}
                {cardFee > 0 && (
                  <div className="price-row">
                    <span className="label">Card Processing Fee</span>
                    <span className="value fee">+${cardFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="price-row total">
                  <span className="label">Total</span>
                  <span className="value">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="checkbox-row" onClick={() => setTermsAccepted(!termsAccepted)}>
                <div className={`checkbox ${termsAccepted ? 'checked' : ''}`}>
                  {termsAccepted && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <span className="checkbox-label">
                  By making this purchase, you agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    terms of service
                  </a>
                </span>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={resetModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateOrder} disabled={!canCreateOrder}>
                {orderLoading ? <span className="loading-spinner"></span> : 'Pay Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
